/**
 *
 * @module samsungTizenPlayerAdapter
 * @desc PlayerAdapter for Samsung Tizen TVs
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/media.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/stream-sample.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerEvents.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerProperties.md
 * @see http://developer.samsung.com/tv/develop/api-references/samsung-product-api-references/avplay-api
 *
 */

import Check from 'check-types';
import Services from './../../services/entry';
import Drm from './../../drm/entry';
import PlayerAdapter from './../playerAdapter';
import PlaybackMetrics from './../playbackMetrics';
import PlaybackEventListener from './../playbackEventListener';

const DrmProvider = Drm.DrmProvider;

const ErrorReason = Services.Exception.ErrorReason;
const ExceptionReference = Services.Exception.ExceptionReference;
const ServiceException = Services.Exception.ServiceException;
const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    object,
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class SamsungTizenPlayerAdapter
 * @extends {PlayerAdapter}
 * @desc Interface used to communicate with the media player.
 *
 */
export default class SamsungTizenPlayerAdapter extends PlayerAdapter {

    /**
     *
     * @param {NativePlayer} nativePlayer
     * @param {Object|Null} [clientListener=null]
     * @throws {InvalidArgumentException}
     * @note The SamsungTizenPlayerAdapter requires nativePlayer.setStreamingProperty,
     * nativePlayer.open, and nativePlayer.setListener
     *
     */
    constructor(nativePlayer, clientListener = null) {

        const errorMessage = {
            ctor: 'SamsungTizenPlayerAdapter(nativePlayer, clientListener)',
            get nativePlayer() {
                return `${this.ctor} needs NativePlayer instance`;
            },
            get clientListener() {
                return `${this.ctor} needs clientListener instance`;
            }
        };

        const nativePlayerInstance = (function nativePlayerInstance() {
            return Check.assigned(nativePlayer)
                && Check.function(nativePlayer.setStreamingProperty)
                && Check.function(nativePlayer.setListener)
                && Check.function(nativePlayer.open);
        }());

        Check.assert(nativePlayerInstance, errorMessage.nativePlayer, InvalidArgumentException);

        if (Check.assigned(clientListener)) {
            object(clientListener, errorMessage.clientListener, InvalidArgumentException);
        }

        super(nativePlayer);

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.streamingProperty = null;

        /**
         *
         * @access public
         * @type {Object}
         * @desc Exposed listeners for when application developer needs to call
         * nativePlayer.setListener after a playbackSession.prepare
         * @since 2.2.1
         *
         */
        this.playerListener = null;

        /**
         *
         * @access private
         * @type {Object}
         * @desc Listeners that are set by application developer
         * @since 2.2.1
         *
         */
        this.clientListener = clientListener;

    }

    /**
     *
     * @access public
     * @param {String} playlistUri - The URL to be used during playback.
     * @desc Callback used when prepare has been called (usually via the {PlaybackSession}).
     * Sets the source URI on the {NativePlayer} instance.
     * @throws {InvalidStateException} Unable to set playlistUri on NativePlayer
     * @returns {Promise<Void>}
     *
     */
    setSource(playlistUri) {

        const errorMessage = {
            method: 'SamsungTizenPlayerAdapter.setSource(playlistUri)',
            get playlistUri() {
                return `${this.method} playlistUri should be a String with length`;
            },
            get accessToken() {
                return `${this.method} needs valid accessToken`;
            },
            get setPlaylistUri() {
                return `${this.method} unable to set playlistUri on NativePlayer`;
            }
        };

        nonEmptyString(playlistUri, errorMessage.playlistUri, InvalidArgumentException);

        const { accessToken, nativePlayer } = this;

        return new Promise((resolve, reject) => {

            this.playlistUri = playlistUri;

            try {

                if (Check.assigned(accessToken)) {

                    const streamingProperty = `|COOKIE={Authorization=${accessToken};}`;

                    this.playlistUri = playlistUri;
                    this.streamingProperty = streamingProperty;

                    nativePlayer.open(playlistUri);
                    nativePlayer.setStreamingProperty('ADAPTIVE_INFO', streamingProperty);

                    return resolve();

                }

                const reasons = [new ErrorReason('', errorMessage.accessToken)];
                const exceptionData = ExceptionReference.common.invalidState;

                return reject(new ServiceException({ reasons, exceptionData }));

            } catch (ex) {

                const reasons = [new ErrorReason('', `${errorMessage.setPlaylistUri} - ${ex}`)];
                const exceptionData = ExceptionReference.common.invalidState;

                return reject(new ServiceException({ reasons, exceptionData }));

            }

        });

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @desc Gets a snapshot of information about media playback.
     * @throws {InvalidStateException} Unable to get NativePlayer playhead or bitrate data
     * @returns {Promise<PlaybackMetrics>} - instance that contains a snapshot
     * of information about media playback.
     * @note nativePlayer.getCurrentTime - Time returned in milliseconds.
     * @note metric value is rounded down to prevent possible service issues with floats
     * @note executed by {PlaybackTelemetryDispatcher#recordStreamSample}
     *
     */
    getPlaybackMetrics() {

        const errorMessage = {
            method: 'SamsungTizenPlayerAdapter.getPlaybackMetrics()',
            get currentPlayhead() {
                return `${this.method} unable to get NativePlayer playhead data`;
            },
            get currentBitrate() {
                return `${this.method} unable to get NativePlayer bitrate data`;
            }
        };

        const { nativePlayer, getBitrate, unavailableMetric } = this;

        if (Check.not.function(nativePlayer.getCurrentStreamInfo)) {

            const reasons = [new ErrorReason('', errorMessage.currentBitrate)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        if (Check.not.function(nativePlayer.getCurrentTime)) {

            const reasons = [new ErrorReason('', errorMessage.currentPlayhead)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        const currentStreamInfo = nativePlayer.getCurrentStreamInfo();
        const currentBitrate = Math.floor(getBitrate(currentStreamInfo)) || unavailableMetric;
        const currentPlayhead = Math.floor(nativePlayer.getCurrentTime() / 1000) || unavailableMetric;

        return Promise.resolve(new PlaybackMetrics(currentBitrate, currentPlayhead));

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @param {PlaybackEventListener} listener
     * @throws {InvalidStateException} Unable to add PlaybackEventListener
     * @returns {Promise<Void>}
     * @note Samsung Tizen has several events that can be fired by the player.
     * Callbacks for these events are defined and then set to the player.
     *
     */
    addListener(listener) {

        const errorMessage = {
            method: 'SamsungTizenPlayerAdapter.addListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            },
            get addListener() {
                return `${this.method} unable to add PlaybackEventListener`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { boundHandlers, nativePlayer, clientListener } = this;
        const { onPlay, onError, onPlayedToCompletion } = this;

        return new Promise((resolve, reject) => {

            if (Check.function(nativePlayer.setListener)) {

                this.listener = listener;

                boundHandlers.onPlay = onPlay.bind(this);
                boundHandlers.onError = onError.bind(this);
                boundHandlers.onPlayedToCompletion = onPlayedToCompletion.bind(this);

                const playerListener = {
                    onbufferingstart: boundHandlers.onPlay,
                    onerror: boundHandlers.onError,
                    onstreamcompleted: boundHandlers.onPlayedToCompletion
                };

                if (Check.assigned(clientListener)) {

                    for (const key in clientListener) { // eslint-disable-line

                        const hasClientListener = Check.assigned(clientListener[key]) && Check.function(clientListener[key]); // eslint-disable-line
                        const hasPlayerListener = Check.assigned(playerListener[key]) && Check.function(playerListener[key]); // eslint-disable-line

                        if (hasClientListener && hasPlayerListener) {

                            playerListener[key] = (value) => {

                                clientListener[key](value);
                                playerListener[key](value);

                            };

                        }

                        if (hasClientListener && !hasPlayerListener) {
                            playerListener[key] = clientListener[key];
                        }

                    }

                }

                this.playerListener = playerListener;

                nativePlayer.setListener(this.playerListener);

                return resolve();

            }

            const reasons = [new ErrorReason('', errorMessage.addListener)];
            const exceptionData = ExceptionReference.common.invalidState;

            return reject(new ServiceException({ reasons, exceptionData }));

        });

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @param {PlaybackEventListener} listener
     * @returns {Promise<Void>}
     *
     */
    removeListener(listener) {

        const errorMessage = {
            method: 'SamsungTizenPlayerAdapter.removeListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { nativePlayer } = this;

        return new Promise((resolve) => {

            if (Check.function(nativePlayer.setListener) && Check.equal(this.listener, listener)) {

                this.listener = null;
                this.playerListener = null;

                nativePlayer.onbufferingstart = null;
                nativePlayer.onerror = null;
                nativePlayer.onstreamcompleted = null;

            }

            return resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.2.0
     * @param {SDK.Drm.DrmProvider} drmProvider
     * @returns {Promise<Void>}
     *
     */
    setDrmProvider(drmProvider) {

        const errorMessage = {
            method: 'SamsungTizenPlayerAdapter.setDrmProvider(drmProvider)',
            get drmProvider() {
                return `${this.method} needs SDK.Drm.DrmProvider instance`;
            }
        };

        instanceStrict(drmProvider, DrmProvider, errorMessage.drmProvider, InvalidArgumentException);

        this.drmProvider = drmProvider;

        return Promise.resolve();

    }

    /**
     *
     * @access public
     * @desc Resets player adapter state. Removes all playback listeners.
     * @returns {Promise<Void>}
     *
     */
    clean() {

        const { listener } = this;

        this.playlistUri = '';
        this.boundHandlers = {};
        this.drmProvider = null;

        if (Check.assigned(listener)) {
            return this.removeListener(listener);
        }

        return Promise.resolve();

    }

    /* #region private: */

    /**
     *
     * @access public
     * @since 3.1.0
     * @desc Completes the cleanup process by completely cleaning up all `PlayerAdapter`
     * references. This method should be executed by the application developer
     * when they no longer need to use a `PlayerAdapter` instance.
     * @note Because `clean` is automatically called by the `PlaybackSession` instance,
     * another method is necessary to handle the use case of an application developer who
     * wants to continue using a `PlayerAdapter` instance after a `PlaybackSession` has been released.
     * @returns {Promise<Void>}
     *
     */
    dispose() {

        this.nativePlayer = null;
        this.accessToken = null;
        this.playerListener = null;

        return Promise.resolve();

    }

    /**
     *
     * @access private
     * @since 2.0.0
     * @returns {Promise<Void>}
     * @todo implement #addListener onPlay and onPause via #setState()
     *
     */
    // onPlayerDidPause() {
    //
    //     const { nativePlayer } = this;
    //
    //     return new Promise((resolve) => {
    //
    //         // nativePlayer.getState();
    //         // "NONE", "IDLE", "READY", "PLAYING" or "PAUSED" is logged.
    //
    //         resolve();
    //
    //     });
    //
    // }

    /**
     *
     * @access private
     * @since 2.0.0
     * @param {Array} currentStreamInfo
     * @returns {Number|Null}
     *
     */
    getBitrate(currentStreamInfo) {

        let bitrate = null;

        for (let i = 0; i < currentStreamInfo.length; i += 1) {

            if (currentStreamInfo[i].type === 'VIDEO') {
                const extraInfo = JSON.parse(currentStreamInfo[i].extra_info);
                bitrate = parseInt(extraInfo.Bit_rate, 10);
            }

        }

        return bitrate;

    }

}



// WEBPACK FOOTER //
// ./src/media/playerAdapter/samsungTizenPlayerAdapter.js